import * as echarts from "echarts"
import type { ScoreTrendSeries, InstitutionYearSummary, MajorTrendSeries } from "ncee_client/types"

const MACARONS_COLORS = [
  "#2ec7c9", "#b6a2de", "#5ab1ef", "#ffb980", "#d87a80",
  "#8d98b3", "#e5cf0d", "#97b552", "#95706d", "#dc69aa",
  "#07a2a4", "#9a7fd1", "#588dd5", "#f5994e", "#c05050",
]

const MACARONS_THEME = {
  color: MACARONS_COLORS,
}

echarts.registerTheme("macarons", MACARONS_THEME)

const CHART_WIDTH = 800
const CHART_HEIGHT = 400
const CHART_HEIGHT_BAR = 320

const COLOR_PRIMARY = "#4096ff"
const COLOR_SECONDARY = "#7cb8ff"
const COLOR_BAND = "rgba(64, 150, 255, 0.12)"
const COLOR_MUTED = "#64748b"
const COLOR_GRID = "rgba(15, 23, 42, 0.08)"
const COLOR_TITLE = "#1f6fe5"

function formatScore(params: { value: number | null }): string {
  return params.value != null ? String(Math.round(params.value)) : ""
}

export function renderScoreTrendSvg(trend: ScoreTrendSeries): string {
  const chart = echarts.init(null, "macarons", {
    renderer: "svg",
    ssr: true,
    width: CHART_WIDTH,
    height: CHART_HEIGHT,
  })

  chart.setOption({
    title: {
      text: `${trend.province} · ${trend.subject_type} · ${trend.batch_name}`,
      left: 4,
      top: 4,
      textStyle: { fontSize: 13, color: COLOR_TITLE, fontWeight: 500 },
    },
    legend: {
      data: ["最低分", "平均分"],
      top: 4,
      right: 4,
      textStyle: { fontSize: 12, color: COLOR_MUTED },
    },
    animation: false,
    grid: { top: 36, right: 40, bottom: 40, left: 55 },
    xAxis: {
      type: "category",
      data: trend.years.map(String),
      axisLabel: { fontSize: 12, color: COLOR_MUTED },
      axisLine: { lineStyle: { color: COLOR_GRID } },
      axisTick: { show: false },
    },
    yAxis: {
      type: "value",
      scale: true,
      min: (value: { min: number; max: number }) => Math.floor(value.min - (value.max - value.min) * 0.2),
      max: (value: { min: number; max: number }) => Math.ceil(value.max + (value.max - value.min) * 0.3),
      axisLabel: { fontSize: 12, color: COLOR_MUTED },
      splitLine: { lineStyle: { color: COLOR_GRID } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    labelLayout: {
      moveOverlap: "shiftY",
    },
    series: [
      {
        name: "平均分",
        type: "line",
        data: trend.avg_scores,
        smooth: true,
        symbol: "circle",
        symbolSize: 5,
        lineStyle: { width: 1.5 },
        label: {
          show: true,
          position: "bottom",
          fontSize: 11,
          formatter: formatScore,
        },
      },
      {
        name: "最低分",
        type: "line",
        data: trend.min_scores,
        smooth: true,
        symbol: "circle",
        symbolSize: 5,
        lineStyle: { width: 1.5 },
        label: {
          show: true,
          position: "top",
          fontSize: 11,
          formatter: formatScore,
        },
      },
    ],
  })

  const svg = chart.renderToSVGString()
  chart.dispose()
  return svg
}

export function getDirectionLabel(direction: string): string {
  const labels: Record<string, string> = {
    rising: "↑ 上升",
    falling: "↓ 下降",
    stable: "→ 平稳",
    insufficient_data: "数据不足",
  }
  return labels[direction] || "—"
}

export function getConfidenceLabel(confidence: string): string {
  const labels: Record<string, string> = { high: "高", medium: "中", low: "低" }
  return labels[confidence] || "—"
}

export function getDirectionClass(direction: string): string {
  if (direction === "rising") return "trend-up"
  if (direction === "falling") return "trend-down"
  return "trend-stable"
}

const BAR_COLORS = [
  "#1f6fe5", "#4096ff", "#5aa7ff", "#7cb8ff", "#97c8ff",
  "#2e83f0", "#3b99ff", "#74b4ff", "#8dc2ff", "#b7d7ff",
]

export function renderMajorScoreRangeSvg(
  majorSummaries: { major_name: string; latest_min_score: number | null; latest_max_score: number | null }[],
): string {
  // Filter to majors with both min and max, take top 15 by min score
  const majors = majorSummaries
    .filter((m) => m.latest_min_score != null && m.latest_max_score != null)
    .sort((a, b) => (b.latest_min_score ?? 0) - (a.latest_min_score ?? 0))
    .slice(0, 15)
    .reverse() // lowest at bottom, highest at top

  if (majors.length === 0) return ""

  const allScores = majors.flatMap((m) => [m.latest_min_score!, m.latest_max_score!])
  const scoreMin = Math.floor(Math.min(...allScores) / 10) * 10 - 10
  const scoreMax = Math.ceil(Math.max(...allScores) / 10) * 10 + 10

  const names = majors.map((m) => m.major_name.length > 16 ? m.major_name.slice(0, 16) + "…" : m.major_name)

  const chart = echarts.init(null, "macarons", {
    renderer: "svg",
    ssr: true,
    width: 800,
    height: Math.max(280, majors.length * 28 + 60),
  })

  chart.setOption({
    color: BAR_COLORS,
    title: {
      text: "专业录取分数范围（最新年份）",
      left: 4,
      top: 4,
      textStyle: { fontSize: 13, color: COLOR_TITLE, fontWeight: 500 },
    },
    animation: false,
    grid: { top: 36, right: 60, bottom: 30, left: 160 },
    xAxis: {
      type: "value",
      min: scoreMin,
      max: scoreMax,
      axisLabel: { fontSize: 12, color: COLOR_MUTED },
      splitLine: { lineStyle: { color: COLOR_GRID } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: "category",
      data: names,
      axisLabel: { fontSize: 12, color: COLOR_MUTED },
      axisLine: { lineStyle: { color: COLOR_GRID } },
      axisTick: { show: false },
    },
    series: [
      // Invisible bar for the offset (min score position)
      {
        type: "bar",
        stack: "score",
        data: majors.map((m) => m.latest_min_score),
        itemStyle: { color: "transparent" },
        barWidth: 16,
      },
      // Visible bar for the range (min to max)
      {
        type: "bar",
        stack: "score",
        data: majors.map((m) => m.latest_max_score! - m.latest_min_score!),
        itemStyle: {
          color: COLOR_PRIMARY,
          borderRadius: [0, 3, 3, 0],
        },
        barWidth: 16,
        label: {
          show: true,
          position: "right",
          fontSize: 12,
          color: COLOR_MUTED,
          formatter: (params: { dataIndex: number }) => {
            const m = majors[params.dataIndex]
            return m.latest_min_score === m.latest_max_score
              ? String(Math.round(m.latest_min_score!))
              : `${Math.round(m.latest_min_score!)}–${Math.round(m.latest_max_score!)}`
          },
        },
      },
    ],
  })

  const svg = chart.renderToSVGString()
  chart.dispose()
  return svg
}

export function renderMajorScoreSvg(trend: MajorTrendSeries): string {
  const chart = echarts.init(null, "macarons", {
    renderer: "svg",
    ssr: true,
    width: CHART_WIDTH,
    height: CHART_HEIGHT,
  })

  chart.setOption({
    title: {
      text: trend.major_name + " · 分数走势",
      left: 4,
      top: 4,
      textStyle: { fontSize: 13, color: COLOR_TITLE, fontWeight: 500 },
    },
    legend: {
      data: ["最高分", "平均分", "最低分"],
      top: 4,
      right: 4,
      textStyle: { fontSize: 12, color: COLOR_MUTED },
    },
    animation: false,
    grid: { top: 36, right: 40, bottom: 40, left: 55 },
    xAxis: {
      type: "category",
      data: trend.years.map(String),
      axisLabel: { fontSize: 12, color: COLOR_MUTED },
      axisLine: { lineStyle: { color: COLOR_GRID } },
      axisTick: { show: false },
    },
    yAxis: {
      type: "value",
      scale: true,
      min: (value: { min: number; max: number }) => Math.floor(value.min - (value.max - value.min) * 0.2),
      max: (value: { min: number; max: number }) => Math.ceil(value.max + (value.max - value.min) * 0.3),
      axisLabel: { fontSize: 12, color: COLOR_MUTED },
      splitLine: { lineStyle: { color: COLOR_GRID } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    labelLayout: { moveOverlap: "shiftY" },
    series: [
      {
        name: "最高分",
        type: "line",
        data: trend.max_scores,
        smooth: true,
        symbol: "circle",
        symbolSize: 5,
        lineStyle: { width: 1.5 },
        label: { show: true, position: "top", fontSize: 11, formatter: formatScore },
      },
      {
        name: "平均分",
        type: "line",
        data: trend.avg_scores,
        smooth: true,
        symbol: "circle",
        symbolSize: 5,
        lineStyle: { width: 1.5 },
        label: { show: true, position: "bottom", fontSize: 11, formatter: formatScore },
      },
      {
        name: "最低分",
        type: "line",
        data: trend.min_scores,
        smooth: true,
        symbol: "circle",
        symbolSize: 5,
        lineStyle: { width: 1.5 },
        label: { show: true, position: "top", fontSize: 11, formatter: formatScore },
      },
    ],
  })

  const svg = chart.renderToSVGString()
  chart.dispose()
  return svg
}

export function renderMajorRankSvg(trend: MajorTrendSeries): string {
  const chart = echarts.init(null, "macarons", {
    renderer: "svg",
    ssr: true,
    width: CHART_WIDTH,
    height: CHART_HEIGHT,
  })

  chart.setOption({
    title: {
      text: trend.major_name + " · 位次走势",
      left: 4,
      top: 4,
      textStyle: { fontSize: 13, color: COLOR_TITLE, fontWeight: 500 },
    },
    legend: {
      data: ["最低位次"],
      top: 4,
      right: 4,
      textStyle: { fontSize: 12, color: COLOR_MUTED },
    },
    animation: false,
    grid: { top: 36, right: 40, bottom: 40, left: 65 },
    xAxis: {
      type: "category",
      data: trend.years.map(String),
      axisLabel: { fontSize: 12, color: COLOR_MUTED },
      axisLine: { lineStyle: { color: COLOR_GRID } },
      axisTick: { show: false },
    },
    yAxis: {
      type: "value",
      scale: true,
      inverse: true,
      axisLabel: { fontSize: 12, color: COLOR_MUTED },
      splitLine: { lineStyle: { color: COLOR_GRID } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    labelLayout: { moveOverlap: "shiftY" },
    series: [
      {
        name: "最低位次",
        type: "line",
        data: trend.min_ranks,
        smooth: true,
        symbol: "circle",
        symbolSize: 5,
        lineStyle: { width: 1.5 },
        label: {
          show: true,
          position: "bottom",
          fontSize: 11,
          formatter: (params: { value: number | null }) => params.value != null ? String(Math.round(params.value)) : "",
        },
      },
    ],
  })

  const svg = chart.renderToSVGString()
  chart.dispose()
  return svg
}

export function getMajorScoreChartOption(trend: MajorTrendSeries): object {
  return {
    title: {
      text: trend.major_name + " · 分数走势",
      left: 4, top: 4,
      textStyle: { fontSize: 13, color: COLOR_TITLE, fontWeight: 500 },
    },
    legend: {
      data: ["最高分", "平均分", "最低分"],
      top: 4, right: 4,
      textStyle: { fontSize: 12, color: COLOR_MUTED },
    },
    animation: false,
    grid: { top: 36, right: 40, bottom: 40, left: 55 },
    xAxis: {
      type: "category",
      data: trend.years.map(String),
      axisLabel: { fontSize: 12, color: COLOR_MUTED },
      axisLine: { lineStyle: { color: COLOR_GRID } },
      axisTick: { show: false },
    },
    yAxis: {
      type: "value", scale: true,
      min: (value: { min: number; max: number }) => Math.floor(value.min - (value.max - value.min) * 0.2),
      max: (value: { min: number; max: number }) => Math.ceil(value.max + (value.max - value.min) * 0.3),
      axisLabel: { fontSize: 12, color: COLOR_MUTED },
      splitLine: { lineStyle: { color: COLOR_GRID } },
      axisLine: { show: false }, axisTick: { show: false },
    },
    labelLayout: { moveOverlap: "shiftY" },
    series: [
      {
        name: "最高分", type: "line", data: trend.max_scores,
        smooth: true, symbol: "circle", symbolSize: 5, lineStyle: { width: 1.5 },
        label: { show: true, position: "top", fontSize: 11, formatter: (p: { value: number | null }) => p.value != null ? String(Math.round(p.value)) : "" },
      },
      {
        name: "平均分", type: "line", data: trend.avg_scores,
        smooth: true, symbol: "circle", symbolSize: 5, lineStyle: { width: 1.5 },
        label: { show: true, position: "bottom", fontSize: 11, formatter: (p: { value: number | null }) => p.value != null ? String(Math.round(p.value)) : "" },
      },
      {
        name: "最低分", type: "line", data: trend.min_scores,
        smooth: true, symbol: "circle", symbolSize: 5, lineStyle: { width: 1.5 },
        label: { show: true, position: "top", fontSize: 11, formatter: (p: { value: number | null }) => p.value != null ? String(Math.round(p.value)) : "" },
      },
    ],
  }
}

export function getMajorRankChartOption(trend: MajorTrendSeries): object {
  return {
    title: {
      text: trend.major_name + " · 位次走势",
      left: 4, top: 4,
      textStyle: { fontSize: 13, color: COLOR_TITLE, fontWeight: 500 },
    },
    legend: {
      data: ["最低位次"],
      top: 4, right: 4,
      textStyle: { fontSize: 12, color: COLOR_MUTED },
    },
    animation: false,
    grid: { top: 36, right: 40, bottom: 40, left: 65 },
    xAxis: {
      type: "category",
      data: trend.years.map(String),
      axisLabel: { fontSize: 12, color: COLOR_MUTED },
      axisLine: { lineStyle: { color: COLOR_GRID } },
      axisTick: { show: false },
    },
    yAxis: {
      type: "value", scale: true, inverse: true,
      axisLabel: { fontSize: 12, color: COLOR_MUTED },
      splitLine: { lineStyle: { color: COLOR_GRID } },
      axisLine: { show: false }, axisTick: { show: false },
    },
    labelLayout: { moveOverlap: "shiftY" },
    series: [
      {
        name: "最低位次", type: "line", data: trend.min_ranks,
        smooth: true, symbol: "circle", symbolSize: 5, lineStyle: { width: 1.5 },
        label: {
          show: true, position: "bottom", fontSize: 11,
          formatter: (p: { value: number | null }) => p.value != null ? String(Math.round(p.value)) : "",
        },
      },
    ],
  }
}
